import React, { useEffect, useMemo, useState } from 'react';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { useNavigate } from 'react-router-dom';
import axiosInstance from '../utils/axiosInstance';
import WorkspaceCard from './workspaces/WorkspaceCard';
import {
  filterWorkspaceCards,
  normalizeWorkspaceCard,
} from './workspaces/workspaceViewModel';

export const DEFAULT_WORKSPACE_FILTERS = { status: 'active', search: '' };

export const useWorkspaceList = ({ endpoint, errorMessage }) => {
  const [workspaces, setWorkspaces] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    let isActive = true;

    const fetchWorkspaces = async () => {
      setLoading(true);
      setError('');
      try {
        const response = await axiosInstance.get(endpoint);
        const workspaceList = Array.isArray(response.data?.workspaces) ? response.data.workspaces : [];
        if (isActive) setWorkspaces(workspaceList);
      } catch (err) {
        if (!isActive) return;
        console.error('Failed to fetch workspaces', err);
        setError(errorMessage);
      } finally {
        if (isActive) setLoading(false);
      }
    };

    fetchWorkspaces();
    return () => { isActive = false; };
  }, [endpoint, errorMessage]);

  return { workspaces, loading, error, setWorkspaces, setError };
};

// Retained for callers that filter an API response before normalizing it.
export const filterWorkspaces = (workspaces, filters = {}) => {
  const status = filters.status || 'all';
  const search = String(filters.search || '').trim().toLowerCase();
  return workspaces.filter((workspace) => {
    const normalized = normalizeWorkspaceCard(workspace);
    const matchesStatus = status === 'all'
      || (status === 'deactivated' && normalized.isArchived)
      || normalized.lifecycle === status;
    return matchesStatus && (!search || normalized.searchText.includes(search));
  });
};

export { default as WorkspaceCard } from './workspaces/WorkspaceCard';

export default function WorkspacesTableView({
  endpoint = '/requests/workspaces',
  workspaceBasePath = '/workspaces',
  errorMessage = 'Unable to load workspaces right now.',
}) {
  const navigate = useNavigate();
  const [search, setSearch] = useState('');
  const { workspaces, loading, error } = useWorkspaceList({ endpoint, errorMessage });
  const normalized = useMemo(
    () => workspaces.map(normalizeWorkspaceCard),
    [workspaces],
  );
  const visible = useMemo(
    () => filterWorkspaceCards(normalized, { tab: 'active', search }),
    [normalized, search],
  );

  if (loading) {
    return (
      <Stack alignItems="center" spacing={1.5} sx={{ py: 6 }}>
        <CircularProgress size={30} />
        <Typography color="text.secondary">Loading workspaces...</Typography>
      </Stack>
    );
  }
  if (error) return <Alert severity="error">{error}</Alert>;

  return (
    <Box>
      <Box
        component="input"
        value={search}
        onChange={(event) => setSearch(event.target.value)}
        aria-label="Search workspaces"
        sx={{ mb: 2, p: 1, width: '100%', maxWidth: 360 }}
      />
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', lg: 'repeat(2, minmax(0, 1fr))' },
          gap: 2,
        }}
      >
        {visible.map((workspace) => (
          <WorkspaceCard
            key={workspace.id}
            workspace={workspace}
            onOpen={() => navigate(`${workspaceBasePath.replace(/\/$/, '')}/${workspace.id}`)}
          />
        ))}
      </Box>
    </Box>
  );
}
